import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Select, SelectItem } from "@carbon/react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../components/carbon/DataTable/DataTable";
import EmptyState from "../../components/carbon/EmptyState/EmptyState";
import ConfirmDialog from "../../components/carbon/ConfirmDialog/ConfirmDialog";
import { TagBadge } from "../../components";
import { useGetTags, useBulkAssignTags } from "../../hooks/useTags";
import {
  useAllCompanyMaterials,
  useSearchAllMaterials,
} from "../../hooks/useMaterials";
import useDebounce from "../../hooks/useDebounce";
import { useNotification } from "../../context";
import {
  MaterialType,
  MaterialResponse,
  MaterialWithSuppliersResponse,
} from "../../types";
import "./bulkAssignTagsTab.scss";

// `/material/company/name/{type}/{page}` and `/material/company/search/{name}/{query}/{page}`
// (MaterialController.java) both hardcode `PageRequest.of(page, 10, ...)` server-side — there is
// no `size` request param either endpoint reads, so this is a fixed floor, not a UI choice, same
// as Tags.tsx's own PAGE_SIZE comment for `/tags/paginated`'s default.
const PAGE_SIZE = 10;

interface MaterialRow extends DataTableRow {
  id: string;
  name: string;
  category: string;
  currentTags: React.ReactNode;
}

/**
 * The materials multi-select half of the Bulk Assign Tags tab, rebuilt (Task 5) on the real
 * `DataTable` `selection` prop (Task 3) in place of the 527-line Tailwind carry-over this
 * replaces (`git log` — the previous `BulkAssignTagsTab.tsx` — has the byte-for-byte original).
 * Same user-facing shape as before: pick tag(s), pick material(s) from a searchable/paginated/
 * type-filtered list, submit to the (now company-scoped-on-both-axes, `@NotEmpty`-guarded)
 * `POST /material/bulk/tags`.
 *
 * Materials search + pagination are wired SERVER-SIDE through `DataTable`'s own `search`/
 * `pagination` props, not invented client-side filtering: `useAllCompanyMaterials`/
 * `useSearchAllMaterials` (hooks/useMaterials.ts, untouched — shared with the unmigrated
 * Materials flow) already hit paginated, query-aware endpoints, exactly the shape Sites.tsx/
 * Tags.tsx already consume the same way. The list/search response shapes differ (`MaterialCompany`
 * wrapper vs. flat `Material`), so `displayedMaterials` below normalizes them the same way the
 * pre-Carbon tab did — that normalization is genuine domain logic, not something either hook (or
 * `DataTable`) should own.
 *
 * Tag choice stays the pre-Carbon toggle-chip pattern (a real `<button aria-pressed>` around
 * `TagBadge`, cross-flow and untouched) rather than moving to `SelectField`: this task's brief is
 * the MATERIALS multi-select, and the existing pattern is already a working, accessible multi-pick
 * UI — `TagBadge`'s own visible text is the button's accessible name for free, so no re-litigation
 * bought anything here.
 */
const BulkAssignTagsTab = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();

  const [materialSearchTerm, setMaterialSearchTerm] = useState("");
  const [materialPage, setMaterialPage] = useState(0);
  const [materialType, setMaterialType] = useState<MaterialType>(
    MaterialType.ALL
  );
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<
    Array<string | number>
  >([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { debouncedSearchTerm, isSearching } = useDebounce(
    materialSearchTerm,
    400
  );

  // A new search or a new type filter always restarts at the first page — same convention
  // Sites.tsx/Tags.tsx use for search alone, extended here to the type filter too.
  useEffect(() => {
    setMaterialPage(0);
  }, [debouncedSearchTerm, materialType]);

  const { data: allTags } = useGetTags();

  const { data: materialsData, isLoading: isLoadingMaterials } =
    useAllCompanyMaterials(materialType, materialPage, []);

  const { data: searchedMaterialsData } = useSearchAllMaterials(
    materialType,
    debouncedSearchTerm,
    materialPage,
    []
  );

  // The list endpoint returns `Page<MaterialCompany>` (items shaped like
  // `MaterialWithSuppliersResponse`, with a `.material` field), while the search endpoint returns
  // `Page<Material>` (flat material objects). Normalize the search payload so the rest of this
  // view can rely on a single, consistent shape (`{ material: MaterialResponse, ... }`) — carried
  // over verbatim from the pre-Carbon tab, keyed off the DEBOUNCED term (not the raw one) so the
  // switch lines up with which query is actually `enabled`.
  const displayedMaterials = useMemo(() => {
    const source =
      debouncedSearchTerm && searchedMaterialsData
        ? searchedMaterialsData
        : materialsData;

    // Returning `undefined` rather than `source` here is behaviourally identical to before —
    // this branch only ever fires while `source` itself is undefined (Spring always serializes a
    // `Page`'s `content` as a real array, `[]` included, never omitted) — but it matters to the
    // TYPE now that `useAllCompanyMaterials`/`useSearchAllMaterials` carry explicit generics
    // (Task 5, hooks/useMaterials.ts): returning the raw `source` kept its pre-normalization
    // `MaterialResponse[] | MaterialWithSuppliersResponse[]` content type in the union below,
    // which broke the `rows` mapping's `MaterialWithSuppliersResponse`-only callback further down
    // this file.
    if (!source?.content) return undefined;

    const normalizedContent = source.content.map(
      (item: MaterialWithSuppliersResponse | MaterialResponse) => {
        // Already wrapped (list endpoint shape).
        if (item && "material" in item && (item as any).material) {
          return item as MaterialWithSuppliersResponse;
        }
        // Flat `Material` from the search endpoint — wrap it.
        return {
          material: item as MaterialResponse,
          suppliersQuantity: 0,
          supplierValidatedQuantity: 0,
          activeOrdersQuantity: 0,
        } as MaterialWithSuppliersResponse;
      }
    );

    return { ...source, content: normalizedContent };
  }, [debouncedSearchTerm, searchedMaterialsData, materialsData]);

  const { mutate: bulkAssignTags, isPending: isAssigningTags } =
    useBulkAssignTags(
      () => {
        openNotification(t("tags.bulkAssignSuccess"));
        setConfirmOpen(false);
        setSelectedMaterialIds([]);
        setSelectedTagIds([]);
      },
      () => openNotification(t("tags.bulkAssignError"))
    );

  const handleMaterialTypeChange = (type: MaterialType) => {
    setMaterialType(type);
    // The previous selection may no longer be visible under the new filter — same behaviour the
    // pre-Carbon tab had (its type `<select>`'s own `onChange` cleared `selectedMaterialIds`).
    setSelectedMaterialIds([]);
  };

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleConfirmBulkAssign = () => {
    bulkAssignTags({
      materialIds: selectedMaterialIds.map(String),
      tagIds: selectedTagIds,
    });
  };

  const rows: MaterialRow[] = useMemo(
    () =>
      (displayedMaterials?.content ?? []).map(
        (materialWrapper: MaterialWithSuppliersResponse) => ({
          id: materialWrapper.material.id,
          name: materialWrapper.material.globalMaterialName,
          category: materialWrapper.material.category?.name || "-",
          currentTags:
            materialWrapper.material.tags &&
            materialWrapper.material.tags.length > 0 ? (
              <div className="bulk-assign-tags__row-tags">
                {materialWrapper.material.tags.map((tag) => (
                  <TagBadge key={tag.id} tag={tag} size="sm" />
                ))}
              </div>
            ) : (
              "-"
            ),
        })
      ),
    [displayedMaterials]
  );

  const headers: DataTableColumn<MaterialRow>[] = useMemo(
    () => [
      { key: "name", header: t("materials.name") },
      { key: "category", header: t("materials.category") },
      { key: "currentTags", header: t("tags.currentTags") },
    ],
    [t]
  );

  const materialsEmpty = debouncedSearchTerm ? (
    <EmptyState
      title={t("materials.noResults")}
      action={
        <Button kind="ghost" onClick={() => setMaterialSearchTerm("")}>
          {t("table.clearSearch")}
        </Button>
      }
    />
  ) : (
    <EmptyState title={t("materials.noUploaded")} />
  );

  // Honest partial/empty states: the trigger button is always visible, always tells you why it's
  // disabled rather than firing a toast after the fact (the pre-Carbon tab's own
  // `handleBulkAssign` validation, moved from an on-click toast to a standing, always-visible
  // reason — reusing the exact same copy that toast used to show).
  const canSubmit =
    selectedMaterialIds.length > 0 &&
    selectedTagIds.length > 0 &&
    !isAssigningTags;
  const submitHint =
    selectedMaterialIds.length === 0
      ? t("tags.selectMaterials")
      : selectedTagIds.length === 0
        ? t("tags.selectTagsToAssign")
        : null;

  const selectedTags = useMemo(
    () => (allTags ?? []).filter((tag) => selectedTagIds.includes(tag.id)),
    [allTags, selectedTagIds]
  );

  const confirmBody = (
    <div>
      <p>{t("tags.bulkAssignConfirmMessage")}</p>
      <p>
        {t("tags.materialsSelected", { count: selectedMaterialIds.length })}
      </p>
      <p className="bulk-assign-tags__confirm-label">
        {t("tags.tagsToAssign")}
      </p>
      <div className="bulk-assign-tags__confirm-tags">
        {selectedTags.map((tag) => (
          <TagBadge key={tag.id} tag={tag} size="md" />
        ))}
      </div>
      <p className="bulk-assign-tags__confirm-hint">
        {t("tags.bulkAssignNote")}
      </p>
    </div>
  );

  return (
    <div>
      <div className="bulk-assign-tags__tags-section">
        <p className="bulk-assign-tags__section-label">
          {t("tags.selectTagsToAssign")}{" "}
          <span className="bulk-assign-tags__required">*</span>
        </p>
        {allTags && allTags.length > 0 ? (
          <div
            className="bulk-assign-tags__tag-list"
            role="group"
            aria-label={t("tags.selectTagsToAssign")}
          >
            {allTags.map((tag) => {
              const selected = selectedTagIds.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  aria-pressed={selected}
                  className={
                    selected
                      ? "bulk-assign-tags__tag-toggle bulk-assign-tags__tag-toggle--selected"
                      : "bulk-assign-tags__tag-toggle"
                  }
                  onClick={() => toggleTag(tag.id)}
                >
                  <TagBadge tag={tag} size="md" />
                </button>
              );
            })}
          </div>
        ) : (
          <p className="bulk-assign-tags__empty-hint">
            {t("tags.noTagsAvailable")}
          </p>
        )}
      </div>

      <div className="bulk-assign-tags__toolbar">
        {/* `role="status"` (implicit `aria-live="polite"`) both announces the running selection
            count to screen reader users as it changes, and gives this exactly one distinguishing
            landmark on the page — `ConfirmDialog`'s own body repeats the same
            `tags.materialsSelected` copy (see `confirmBody` above), and Carbon's `ComposedModal`
            keeps its children mounted (CSS-hidden, not unmounted) even while `open={false}`, so a
            bare text match against the page would resolve both and never disambiguate them. */}
        <span className="bulk-assign-tags__toolbar-count" role="status">
          {t("tags.materialsSelected", { count: selectedMaterialIds.length })}
        </span>
        <div className="bulk-assign-tags__toolbar-actions">
          <Button
            kind="ghost"
            disabled={selectedMaterialIds.length === 0}
            onClick={() => setSelectedMaterialIds([])}
          >
            {t("table.clearSelection")}
          </Button>
          <Button disabled={!canSubmit} onClick={() => setConfirmOpen(true)}>
            {t("tags.assignTags")}
          </Button>
        </div>
      </div>
      {submitHint ? (
        <p className="bulk-assign-tags__toolbar-hint">{submitHint}</p>
      ) : null}

      <div className="bulk-assign-tags__filters">
        <Select
          id="bulk-assign-tags-material-type"
          labelText={t("materials.type")}
          value={materialType}
          onChange={(e) =>
            handleMaterialTypeChange(e.target.value as MaterialType)
          }
        >
          <SelectItem value={MaterialType.ALL} text={t("materialTypes.all")} />
          <SelectItem
            value={MaterialType.DIRECT_GOODS}
            text={t("materialTypes.pi")}
          />
          <SelectItem
            value={MaterialType.INDIRECT_GOODS}
            text={t("materialTypes.npi")}
          />
          <SelectItem
            value={MaterialType.SERVICES}
            text={t("materialTypes.services")}
          />
        </Select>
      </div>

      <DataTable
        headers={headers}
        rows={rows}
        isLoading={isLoadingMaterials || isSearching}
        search={{
          value: materialSearchTerm,
          onChange: setMaterialSearchTerm,
          label: t("materials.search"),
        }}
        pagination={{
          page: materialPage + 1,
          pageSize: PAGE_SIZE,
          totalItems: displayedMaterials?.totalElements ?? 0,
          onPageChange: (newPage) => setMaterialPage(newPage - 1),
        }}
        selection={{
          selectedIds: selectedMaterialIds,
          onChange: setSelectedMaterialIds,
          getRowLabel: (row) => t("tags.selectMaterial", { name: row.name }),
          selectAllLabel: t("tags.selectAllMaterials"),
        }}
        empty={materialsEmpty}
      />

      <ConfirmDialog
        open={confirmOpen}
        title={t("tags.confirmBulkAssign")}
        body={confirmBody}
        confirmLabel={t("tags.confirmAssign")}
        onConfirm={handleConfirmBulkAssign}
        onCancel={() => setConfirmOpen(false)}
        isPending={isAssigningTags}
      />
    </div>
  );
};

export default BulkAssignTagsTab;
